/**
 * **Important Notice**
 *
 * This file is currently being refactored as part of a migration process. Common logic and utility functions will be relocated to dedicated files to support both Vue 2.x and Vue 3 implementations. Please ensure that you update your code accordingly to reflect these changes.
 *
 * ### Key Points:
 * 1. **Vue 2.x Implementation:**
 *    - This file is specific to the Vue 2.x implementation.
 *    - It will be deprecated and removed in future releases.
 *
 * 2. **Vue 3 Implementation:**
 *    - For Vue 3, please use the corresponding file located at:
 *      `src/Tv_PrimaryOffersites_Client/Velocity.PrimaryOffersites.Web/wwwroot/js/vue3/services/Payments/ExpressPayments.js`
 *    - Vue 3 will serve as the final implementation in upcoming releases.
 *
 * 3. **Common Code and Utilities:**
 *    - Shared logic will be migrated to the following locations:
 *        - **Parent Class:**
 *          `Common/Payments/ExpressPaymentsBaseService.js`
 *        - **Utility Functions:**
 *          `Common/Payments/TransactCommonUtility.js`
 *    - Any common functionality must be implemented in these files to ensure consistency across Vue 2.x and Vue 3 implementations.
 *    - **Implementation Guidelines:**
 *      - For more details about the parent file and utility file, refer to the comments within those files for specific implementation instructions.
 *
 * For further details or clarification, please reach out to **Leela** directly.
 *
 */

import axios from 'axios';
import * as constants from '../../constants/constants';
import store from '../../../store/store';
import {
  EXPRESS_SET_LEASE_SETTINGS,
  EXPRESS_SET_FINANCE_SETTINGS,
  EXPRESS_SET_CASH_SETTINGS,
  EXPRESS_SET_SERVICE_PROTECTION_PLANS,
  EXPRESS_SET_CREDIT_SCORES,
  EXPRESS_SET_SUMMARY_DATA,
} from '../../../store/types';
import {
  SUMMARY_DATA_DETAILS,
  SET_VEHICLES_LIST_ARRAY,
} from '../../constants/expresspaymentconstants';
import { Payments } from './Payments';
import { Events } from '../Events/Events';
import { sentTradeDetailsProgramme } from '../TransactServices/TransactSummaryService';
import { ExpressPaymentsBaseService } from '../../Common/Payments/ExpressPaymentsBaseService';
import {
  fetchRegionsFromApi,
  getEnabledStepsInTransact,
} from '../../Common/Payments/TransactCommonUtility';
import { fetchVehicleDetailsFromAPI } from '../../Common/Utility/UtilityFunctions';

export class ExpressPayments extends ExpressPaymentsBaseService {
  formatPaymentSettings(paymentSettings) {
    this.dividePaymentSettings(paymentSettings.digitalRetailingSettings);

    store.commit(EXPRESS_SET_SERVICE_PROTECTION_PLANS, paymentSettings.serviceAndProtectionPlans);

    store.commit(EXPRESS_SET_CREDIT_SCORES, {
      selectedPayment: constants.LEASE.toLowerCase(),
      value: paymentSettings.leaseCreditScores,
    });

    store.commit(EXPRESS_SET_CREDIT_SCORES, {
      selectedPayment: constants.FINANCE.toLowerCase(),
      value: paymentSettings.financeCreditScores,
    });
  }

  static getEnabledTransactSteps(selectedPaymentSettings, incentivesData) {
    console.log('get Enabled Transact steps');
    return getEnabledStepsInTransact(selectedPaymentSettings, incentivesData);
  }

  dividePaymentSettings(digitalRetailingSettings) {
    digitalRetailingSettings.forEach((setting, index) => {
      if (setting.paymentType.toLowerCase() === constants.LEASE.toLowerCase()) {
        store.commit(EXPRESS_SET_LEASE_SETTINGS, setting);
      }

      if (setting.paymentType.toLowerCase() === constants.FINANCE.toLowerCase()) {
        store.commit(EXPRESS_SET_FINANCE_SETTINGS, setting);
        store.commit(EXPRESS_SET_CASH_SETTINGS, setting);
      }
    });
  }

  getLeaseSpecialIncentives(zipCode) {
    return this.getSpecialIncentives(constants.LEASE.toLowerCase(), zipCode);
  }

  getFinanceSpecialIncentives(zipCode) {
    return this.getSpecialIncentives(constants.FINANCE.toLowerCase(), zipCode);
  }

  static resetSummaryData() {
    for (let property in SUMMARY_DATA_DETAILS) {
      store.commit(EXPRESS_SET_SUMMARY_DATA, {
        type: property,
        value: '',
      });
    }
  }

  static ConvertPaymentToNumber(value) {
    if (!value) return '';
    value = value.toString();
    value = value.replace('$', '').replace(',', '');
    return +value;
  }

  static async getVehicleDetails(vin = '') {
    try {
      let vehicleDetails = store.getters.getVehicleDetailsByVin(vin);
      if (vehicleDetails) {
        return Promise.resolve(vehicleDetails);
      }

      if (
        window.vehicleDetailsVin &&
        window.vehicleDetailsVin.toLowerCase() === vin.toLowerCase()
      ) {
        let vehicleDetails = store.getters.getVehicleDetailsByVin(vin);
        if (vehicleDetails) {
          return Promise.resolve(vehicleDetails);
        } else {
          return new Promise((resolve) => {
            let count = 1;
            let vehicleInterval = setInterval(() => {
              count++;

              let vehicleDetails = store.getters.getVehicleDetailsByVin(vin);

              if (vehicleDetails) {
                clearInterval(vehicleInterval);
                resolve(vehicleDetails);
              } else if (count > 700) {
                clearInterval(vehicleInterval);
                (async () => {
                  let response = await fetchVehicleDetailsFromAPI(vin);
                  store.commit(SET_VEHICLES_LIST_ARRAY, response.data);
                  resolve(response.data);
                })();
              } else {
                // No action needed for other cases.
              }
            }, 1);
          });
        }
      }

      window.vehicleDetailsVin = vin;

      let response = await fetchVehicleDetailsFromAPI(vin);

      store.commit(SET_VEHICLES_LIST_ARRAY, response.data);
      return response.data;
    } catch (e) {
      console.log('Vehicle Error' + e);
    }
  }

  static async getVehicleDetailsByVin(vin = '') {
    let response = await this.getVehicleDetails(vin);
    return response;
  }

  static vehicleDetails(vin = '') {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise((resolve) => {
      (async () => {
        let response = await this.getVehicleDetails(vin);
        return resolve({ data: response });
      })();
    });
  }

  static async getRegions(zipCode) {
    let response = await fetchRegionsFromApi(zipCode);
    return response.data;
  }

  static getDealerZipCode() {
    if (!window?.zip) return '';
    let dealerZipCode = window.zip;
    return dealerZipCode.split('-')[0];
  }

  static async getAtcPayments(postData) {
    postData.IsOffersites = true;
    let Url = `${atob(window.portalUrl)}OfferManager/Service/PaymentAPI/GetATCPayments`;
    let response = await axios.post(Url, postData);
    return response.data;
  }

  static getSelectedCity(cities, userDetails) {
    for (let i = 0; i < cities?.length; i++) {
      if (cities[i].City.toLowerCase() === userDetails.city.toLowerCase()) {
        return i;
      }
    }
    return '';
  }

  static async getPayments(postData) {
    if (typeof window.vin !== 'undefined') {
      postData.VIN = window.vin;
    }

    if (typeof window.styleId !== 'undefined') {
      postData.StyleId = +window.styleId;
    }

    postData.AccountID = window.vdpAccountId ? +window.vdpAccountId : +window.accountId;
    postData.IsOffersites = true;

    let response = await axios.post(atob(window.paymentapi), postData);
    return response.data;
  }

  static getTotalIncentivesAmount(paymentsData) {
    let amount = 0;

    amount = paymentsData.IncentiveCash + paymentsData.DealerCash;

    return amount;
  }

  static async getCurrentVehicle(consumerId) {
    return await axios.get(`${window.ApiBaseUrl}/Consumer/Details/${consumerId}`);
  }

  static sendNegativeWrappedValue(number) {
    number = Payments.conventStringToNumber(number);
    if (number < 0) {
      return `($${(number * -1).toLocaleString()})`;
    } else {
      return `$${number.toLocaleString()}`;
    }
  }

  static getTotalServiceProtectionPlans(summaryData) {
    let amount = 0;
    for (let item of summaryData.serviceProtectionPlan) {
      if (Number(item.cashPrice)) {
        amount += Payments.conventStringToNumber(item.cashPrice);
      }
    }

    return amount;
  }

  static getTradePaymentValues(details) {
    if (!details) {
      return '';
    }

    if (details.paymentType) {
      if (window.isauthenticated === 'True' && details.paymentType.toLowerCase() === 'lease') {
        if (details.paymentsRemaining && details.monthlyPayment) {
          let val = +details.paymentsRemaining * +details.monthlyPayment;
          if ('residual' in details) {
            val = val + +details.residual;
          }
          return val;
        }
      }

      if (
        details.paymentType.toLowerCase() === 'finance' ||
        details.paymentType.toLowerCase() === 'cash' ||
        details.paymentType.toLowerCase() === 'lease'
      ) {
        if (details.paymentsRemaining && details.monthlyPayment) {
          return +details.paymentsRemaining * +details.monthlyPayment;
        }
      }
    }

    return 0;
  }

  static firePaymentsEvent(eventModel, paymentsData) {
    let paymentsEvent = {
      tradeIn: paymentsData.TradeEquity,
      MSRP: parseFloat(paymentsData.MSRP.replace(/[$,]/g, '')),
      payment: paymentsData.Payment.replace(/[$,]/g, ''),
      incentives: paymentsData.IncentiveCash,
      sellingPrice: parseFloat(paymentsData.PurchasePrice.replace(/[$,]/g, '')),
      paymentDueAtSigning: paymentsData.DueAtSigning.replace(/[$,]/g, ''),
      discount: paymentsData.Discount,
    };
    let eventData = { ...eventModel, ...paymentsEvent };
    Events.logConsumerEvent(eventData);
  }

  static callATCPaymentPaymentBasedOnPaymentType(paymentType = '') {
    if (sentTradeDetailsProgramme()) {
      return true;
    }
    return paymentType.toLowerCase() !== constants.CASH.toLowerCase();
  }
  static getAmountFinanced(payments, showTurnInLease) {
    let value = Payments.conventStringToNumber(payments.PurchasePrice);
    let downpayment = Payments.conventStringToNumber(payments.Downpayment);
    if (downpayment) {
      value = value - downpayment;
    }
    let equity = 0;
    if (+payments.TradeEquity > 0) {
      equity = +payments.TradeEquity;
    } else {
      equity = +payments.TradeEquity * -1;
    }
    if (equity) {
      value = value - equity;
    }
    if (showTurnInLease) {
      value = value + showTurnInLease;
    }
    return value;
  }
}
